import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  MenuItem,
} from '@mui/material';

export default function Users() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);

  const [form, setForm] = useState({
    username: '',
    passwordHash: '',
    email: '',
    name: '',
    role: '',
    status: '1',
  });

  /* ============================
   * Users Load
   * ============================ */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/users');
      const normalized = (res.data || []).map(u => ({
        id: String(u.id),
        username: u.username ?? '',
        email: u.email ?? '',
        name: u.name ?? '',
        role: u.role ?? '',
        status: Number(u.status ?? 1),
        statusLabel: Number(u.status ?? 1) === 1 ? '有効' : '無効',
      }));
      setRows(normalized);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  /* ============================
   * Form
   * ============================ */
  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openEditDialog = user => {
    setEditUser(user);
    setForm({
      username: user.username,
      passwordHash: '',
      email: user.email,
      name: user.name,
      role: user.role,
      status: String(user.status),
    });
    setOpen(true);
  };

  const resetForm = () => {
    setEditUser(null);
    setForm({
      username: '',
      passwordHash: '',
      email: '',
      name: '',
      role: '',
      status: '1',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editUser) {
        const payload = {
          username: form.username,
          email: form.email,
          name: form.name,
          role: form.role,
          status: Number(form.status),
          ...(form.passwordHash ? { passwordHash: form.passwordHash } : {}),
        };
        await api.put(`/api/users/${editUser.id}`, payload);
      } else {
        await api.post('/api/users', {
          ...form,
          status: Number(form.status),
        });
      }
      setOpen(false);
      resetForm();
      loadUsers();
    } catch (e) {
      alert(`保存できませんでした: ${e.response?.data?.error || e.message}`);
    }
  };

  /* ============================
   * Columns（Customers と同じ思想）
   * ============================ */
  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'username', headerName: 'ユーザー名', width: 160 },
    { field: 'email', headerName: 'メールアドレス', width: 240 },
    { field: 'name', headerName: '氏名', width: 140 },
    { field: 'role', headerName: '権限', width: 120 },
    { field: 'statusLabel', headerName: 'ステータス', width: 100 },
    {
      field: 'actions',
      headerName: '操作',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ height: '100%' }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => openEditDialog(params.row)}
          >
            編集
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setDeleteUserId(params.row.id)}
          >
            削除
          </Button>
        </Stack>
      ),
    },
  ];

  /* ============================
   * Render
   * ============================ */
  return (
    <PageContainer>
      <PageHeader
        title="ユーザー管理"
        actions={
          <Button
            variant="contained"
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            + 新規ユーザー
          </Button>
        }
      />

      <Box sx={{ mt: 2, width: '100%', overflowX: 'auto' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{ minWidth: 1000 }}
        />
      </Box>

      {/* 登録・編集 */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editUser ? 'ユーザー編集' : '新規ユーザー追加'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }}>
            <TextField
              label="ユーザーID"
              name="username"
              value={form.username}
              onChange={handleChange}
              required
            />
            <TextField
              label="パスワード"
              name="passwordHash"
              value={form.passwordHash}
              onChange={handleChange}
              type="password"
            />
            <TextField
              label="氏名"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
            <TextField
              label="メールアドレス"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
            />
            <TextField
              select
              label="権限"
              name="role"
              value={form.role}
              onChange={handleChange}
              required
            >
              <MenuItem value="developer">開発管理者</MenuItem>
              <MenuItem value="admin">管理者</MenuItem>
              <MenuItem value="manager">マネージャ</MenuItem>
              <MenuItem value="staff">担当者</MenuItem>
            </TextField>
            <TextField
              select
              label="ステータス"
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <MenuItem value="1">有効</MenuItem>
              <MenuItem value="0">無効</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <Dialog open={Boolean(deleteUserId)} onClose={() => setDeleteUserId(null)}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>このユーザーを削除しますか？</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserId(null)}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                await api.delete(`/api/users/${deleteUserId}`);
                setDeleteUserId(null);
                loadUsers();
              } catch (e) {
                alert(`削除できませんでした: ${e.response?.data?.error || e.message}`);
              }
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
